import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Send, Loader2, ShieldCheck, MessageCircle, Trash2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

/**
 * Star ratings + threaded comments (v79.95).
 *
 * Used for TWO resource kinds:
 *   • listings — `mode="listing"`, `resourceId={listing.id}`
 *   • digital stores — `mode="shop"`, `resourceId={shop.shop_slug}`
 *
 * Only registered users can rate / comment. The owner (listing seller
 * or shop owner) may reply publicly but cannot rate themselves. The
 * "Verified" badge marks every logged-in commenter (founder rule:
 * logged-in users only).
 */
const MODE_CONFIG = {
    listing: {
        listPath: (id) => `/listings/${id}/reviews`,
        ratePath: (id) => `/listings/${id}/rating`,
        commentPath: (id) => `/listings/${id}/comments`,
        replyPath: (cid) => `/comments/${cid}/reply`,
        deletePath: (cid) => `/comments/${cid}`,
        ownerNoun: "seller",
        selfCopy: "You can't rate your own listing",
    },
    shop: {
        listPath: (slug) => `/shops/${slug}/reviews`,
        ratePath: (slug) => `/shops/${slug}/rating`,
        commentPath: (slug) => `/shops/${slug}/comments`,
        replyPath: (cid) => `/shop-comments/${cid}/reply`,
        deletePath: (cid) => `/shop-comments/${cid}`,
        ownerNoun: "shop owner",
        selfCopy: "You can't rate your own shop",
    },
};

export default function ReviewSection({
    // Preferred API (v79.95)
    mode = "listing",
    resourceId,
    ownerId,
    onAggregateChange,
    // Back-compat: older callers passed listingId directly
    listingId,
}) {
    const { user } = useAuth();
    const cfg = MODE_CONFIG[mode] || MODE_CONFIG.listing;
    const rid = resourceId || listingId;

    const [data, setData] = useState(null);
    const [rating, setRating] = useState(0);
    const [hoverStar, setHoverStar] = useState(0);
    const [busyStar, setBusyStar] = useState(false);
    const [text, setText] = useState("");
    const [posting, setPosting] = useState(false);
    const [replyOpen, setReplyOpen] = useState(null);
    const [replyText, setReplyText] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(cfg.listPath(rid));
            setData(data);
            setRating(data.my_stars || 0);
            // v79.95 — Bubble the fresh aggregate up so hosts (Shop.jsx)
            // can refresh their inline rating badge without a reload.
            if (onAggregateChange) onAggregateChange({ avg: data.avg, count: data.count });
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { if (rid) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rid, mode]);

    const isOwner = user && ownerId && user.id === ownerId;
    const canRate = user && !isOwner;

    const setStars = async (n) => {
        if (!user) return toast.info(`Sign in to rate this ${mode === "shop" ? "shop" : "listing"}`);
        if (isOwner) return toast.info(cfg.selfCopy);
        setBusyStar(true);
        try {
            await api.post(cfg.ratePath(rid), { stars: n });
            setRating(n);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyStar(false); }
    };

    const postComment = async () => {
        if (!user) return toast.info("Sign in to leave a comment");
        if (text.trim().length < 2) return toast.error("Comment is too short");
        setPosting(true);
        try {
            await api.post(cfg.commentPath(rid), { text });
            setText("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPosting(false); }
    };

    const postReply = async (cid) => {
        if (replyText.trim().length < 2) return toast.error("Reply is too short");
        try {
            await api.post(cfg.replyPath(cid), { text: replyText });
            setReplyOpen(null); setReplyText("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const removeComment = async (cid) => {
        if (!window.confirm("Delete this comment?")) return;
        try { await api.delete(cfg.deletePath(cid)); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    if (!data) return null;

    return (
        <section className="bg-white border border-aduku-border rounded-xl p-4 sm:p-5 mt-4" data-testid={mode === "shop" ? "shop-review-section" : "review-section"}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                <div>
                    <div className="text-3xl font-display font-bold text-aduku-navy">{data.avg || "—"}</div>
                    <div className="text-xs text-aduku-navy/55">{data.count} rating{data.count === 1 ? "" : "s"}</div>
                </div>
                <div className="flex-1">
                    <div className="text-xs font-semibold text-aduku-navy mb-1">{canRate ? "Your rating" : (isOwner ? cfg.selfCopy : "Sign in to rate")}</div>
                    <div className="flex gap-0.5" onMouseLeave={() => setHoverStar(0)}>
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                key={n}
                                type="button"
                                disabled={!canRate || busyStar}
                                onMouseEnter={() => canRate && setHoverStar(n)}
                                onClick={() => setStars(n)}
                                data-testid={`rate-star-${n}`}
                                className={`p-0.5 ${canRate ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
                            >
                                <Star className={`w-6 h-6 ${(hoverStar || rating) >= n ? "fill-aduku-yellow text-aduku-yellow" : "text-aduku-navy/25"}`} />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-5 border-t border-aduku-border pt-4">
                <h3 className="font-display font-bold text-aduku-navy text-base flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" /> Comments ({data.comments?.length || 0})
                </h3>
                {user ? (
                    <div className="mt-2 flex gap-2">
                        <Textarea
                            data-testid="comment-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder={mode === "shop"
                                ? "Ask a question or share your experience with this shop…"
                                : "Ask a question, share feedback, or leave a review…"}
                            rows={2}
                            maxLength={1000}
                            className="text-sm"
                        />
                        <Button data-testid="comment-submit" onClick={postComment} disabled={posting} className="bg-aduku-navy hover:bg-aduku-navy-800 text-white shrink-0 h-auto">
                            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>
                ) : (
                    <div className="mt-2 text-xs text-aduku-navy/60 bg-aduku-bg rounded-md p-3">Sign in to leave a comment or rating.</div>
                )}
                <div className="mt-3 space-y-3">
                    {(data.comments || []).map((c) => (
                        <div key={c.id} className="border border-aduku-border rounded-lg p-3 text-sm" data-testid={`comment-${c.id}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <span className="font-semibold text-aduku-navy truncate">{c.user_name}</span>
                                    {c.verified_purchase && (
                                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="verified-badge" title="Registered Adukoo user">
                                            <ShieldCheck className="w-3 h-3" /> Verified
                                        </span>
                                    )}
                                </div>
                                {(user?.id === c.user_id || user?.role === "admin") && (
                                    <button onClick={() => removeComment(c.id)} className="text-red-500/70 hover:text-red-600" data-testid={`comment-delete-${c.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                            <div className="text-aduku-navy/85 whitespace-pre-wrap mt-1">{c.text}</div>
                            {c.owner_reply && (
                                <div className="mt-2 ml-3 pl-3 border-l-2 border-aduku-yellow bg-aduku-yellow/5 p-2 rounded-r">
                                    <div className="text-[10px] font-semibold text-aduku-yellow-600 uppercase tracking-wider">{cfg.ownerNoun} reply</div>
                                    <div className="text-aduku-navy/85 whitespace-pre-wrap text-xs mt-0.5">{c.owner_reply}</div>
                                </div>
                            )}
                            {isOwner && !c.owner_reply && (
                                replyOpen === c.id ? (
                                    <div className="mt-2 flex gap-2">
                                        <Textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={`Reply as ${cfg.ownerNoun}…`} className="text-xs" />
                                        <Button onClick={() => postReply(c.id)} data-testid={`comment-reply-submit-${c.id}`} className="bg-aduku-navy text-white shrink-0 h-auto"><Send className="w-4 h-4" /></Button>
                                    </div>
                                ) : (
                                    <button onClick={() => { setReplyOpen(c.id); setReplyText(""); }} className="mt-1 text-[11px] font-semibold text-aduku-navy/70 hover:text-aduku-navy underline" data-testid={`comment-reply-btn-${c.id}`}>Reply as {cfg.ownerNoun}</button>
                                )
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
